// store/slices/chatSlice.ts
import type { PayloadAction} from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk, nanoid } from "@reduxjs/toolkit";
import { logout } from "./userSlice";
import { chatApi } from "../../services/chatApi";
import type { RootState } from "../index";
import type { ChatState, Message, ChatHistoryEntry, SendMessagePayload, SubmitFeedbackPayload, Feedback, ChatAnswerTuple } from "../types/chatTypes";
import type { ChatResponse, ResponseMessage } from "../types/chatTypes";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import readNDJSONStream from "ndjson-readablestream";

/** Shape of NDJSON streaming events from the /chat endpoint */
interface NDJSONStreamEvent {
    answer?: string;
    error?: string;
    done?: boolean;
    conversation_id?: string;
    message_id?: string;
    thoughts?: string;
    data_points?: string[];
    source_urls?: Record<string, string>;
}

const initialState: ChatState = {
    chatHistories: {},
    messages: {},
    messagesByChatHistory: {},
    activeChatHistoryId: null,
    isLoading: false,
    isStreaming: false,
    error: null,
    answers: [],
    streamedAnswers: [],
    lastQuestion: "",
    conversationLogId: ""
};

// ============================================================
// Thunks
// ============================================================

export const fetchChatHistories = createAsyncThunk("chat/fetchChatHistories", async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const userId = state.user.user?.id;

    if (!userId) {
        return rejectWithValue("User not authenticated");
    }

    try {
        return await chatApi.getChatHistories(userId);
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

export const fetchMessages = createAsyncThunk("chat/fetchMessages", async (conversationId: string, { rejectWithValue }) => {
    try {
        const messages = await chatApi.getMessages(conversationId);
        return { conversationId, messages };
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

export const createChatHistory = createAsyncThunk("chat/createChatHistory", async (title: string | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const userId = state.user.user?.id;

    if (!userId) {
        return rejectWithValue("User not authenticated");
    }

    try {
        return await chatApi.createChatHistory(userId, title);
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

export const sendMessage = createAsyncThunk(
    "chat/sendMessage",
    async ({ conversationId, content }: SendMessagePayload, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const userId = state.user.user?.id;

        if (!userId) {
            return rejectWithValue("User not authenticated");
        }

        const userMessageId = nanoid();
        const timestamp = new Date().toISOString();

        dispatch(
            chatSlice.actions.addOptimisticMessage({
                id: userMessageId,
                conversationId,
                role: "user",
                content,
                status: "sent",
                createdAt: timestamp,
                completedAt: timestamp
            })
        );

        try {
            const data = await chatApi.sendMessage(conversationId, content, userId);

            return {
                conversationId,
                userMessage: { ...data.userMessage, id: userMessageId } as Message,
                assistantMessage: data.assistantMessage
            };
        } catch (error) {
            dispatch(
                chatSlice.actions.updateMessageStatus({
                    messageId: userMessageId,
                    status: "error"
                })
            );
            return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
        }
    }
);

export const submitFeedback = createAsyncThunk("chat/submitFeedback", async ({ messageId, type, comment }: SubmitFeedbackPayload, { rejectWithValue }) => {
    try {
        await chatApi.submitMessageFeedback(messageId, type, comment);

        const feedback: Feedback = {
            type,
            comment,
            submittedAt: new Date().toISOString()
        };

        return { messageId, feedback };
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

export const clearChatHistory = createAsyncThunk("chat/clearChatHistory", async (conversationId: string, { rejectWithValue }) => {
    try {
        await chatApi.clearMessages(conversationId);
        return conversationId;
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

export const deleteChatHistory = createAsyncThunk("chat/deleteChatHistory", async (conversationId: string, { rejectWithValue }) => {
    try {
        await chatApi.deleteChatHistory(conversationId);
        return conversationId;
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
    }
});

// ============================================================
// Chat Page Thunks
// ============================================================

export const sendChatMessage = createAsyncThunk(
    "chat/sendChatMessage",
    async ({ question, shouldStream = false, domainSlug }: { question: string; shouldStream?: boolean; domainSlug?: string }, { dispatch, getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const currentAnswers = state.chat.answers;
        const conversationLogId = state.chat.conversationLogId;

        dispatch(chatSlice.actions.setLastQuestion(question));
        dispatch(chatSlice.actions.setError(null));
        dispatch(chatSlice.actions.setLoading(true));

        try {
            const messages: ResponseMessage[] = currentAnswers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].answer, role: "assistant" }
            ]);

            // Send the chat message with conversation_id - backend now handles saving to DB
            const response = await chatApi.sendChatMessage([...messages, { content: question, role: "user" }], conversationLogId || undefined, domainSlug, shouldStream);

            let parsedResponse: ChatResponse;

            if (shouldStream) {
                if (!response.body) {
                    throw new Error("No response body");
                }
                parsedResponse = await handleStreamingResponse(question, currentAnswers, response.body, dispatch);
            } else {
                parsedResponse = await response.json() as ChatResponse;
            }

            // Backend now returns conversation_id and message_id directly in the response
            if (parsedResponse.conversation_id) {
                dispatch(chatSlice.actions.setConversationLogId(parsedResponse.conversation_id));
            }

            // Map the response to include conversation_log for backwards compatibility with UI
            if (parsedResponse.message_id) {
                parsedResponse.conversation_log = {
                    id: parsedResponse.message_id
                };
            }

            return { question, response: parsedResponse };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
        }
    }
);

async function handleStreamingResponse(
    question: string,
    answers: ChatAnswerTuple[],
    responseBody: ReadableStream<Uint8Array>,
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
): Promise<ChatResponse> {
    let answer = "";
    let askResponse: ChatResponse = {} as ChatResponse;
    let conversationId: string | undefined;
    let messageId: string | undefined;

    const updateState = (newContent: string): Promise<void> => {
        return new Promise(resolve => {
            setTimeout(() => {
                answer += newContent;
                const latestResponse: ChatResponse = {
                    ...askResponse,
                    answer: answer
                };
                dispatch(chatSlice.actions.setStreamedAnswers([...answers, [question, latestResponse]]));
                resolve();
            }, 33);
        });
    };

    // Note: setStreaming(false) is NOT dispatched here — the sendChatMessage.fulfilled
    // reducer handles both setStreaming(false) and pushing the final answer in a single
    // synchronous state update, preventing a visual flash between streaming and completed UI.
    dispatch(chatSlice.actions.setStreaming(true));
    for await (const event of readNDJSONStream<NDJSONStreamEvent>(responseBody)) {
        if (event.answer) {
            // Merge fields rather than overwrite. The backend emits some
            // fields (data_points, thoughts, source_urls) on every chunk,
            // but first-chunk-only fields like `selected_domain` (set
            // when the server auto-resolves a base from the user's
            // message) would otherwise be clobbered by the second chunk.
            // The merge preserves them so the dropdown silent-select
            // logic in Chat.tsx actually fires.
            askResponse = { ...askResponse, ...(event as ChatResponse) };
            await updateState(event.answer);
        } else if (event.error) {
            throw new Error(event.error);
        } else if (event.done) {
            // Final chunk with conversation info from backend
            conversationId = event.conversation_id;
            messageId = event.message_id;
        }
    }

    return {
        ...askResponse,
        answer: answer,
        conversation_id: conversationId,
        message_id: messageId
    };
}

export const submitChatFeedback = createAsyncThunk(
    "chat/submitChatFeedback",
    async ({ rating, conversationLogId }: { rating: number; conversationLogId: string }, { rejectWithValue }) => {
        try {
            await chatApi.submitChatFeedback(rating, conversationLogId);
            return { rating, conversationLogId };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
        }
    }
);

export const submitChatAdditionalFeedback = createAsyncThunk(
    "chat/submitChatAdditionalFeedback",
    async (
        {
            conversationLogId,
            feedbackOptions,
            userInput
        }: {
            conversationLogId: string;
            feedbackOptions: { option_id: number; option_text: string }[];
            userInput: string;
        },
        { rejectWithValue }
    ) => {
        try {
            await chatApi.submitChatAdditionalFeedback(feedbackOptions, userInput, conversationLogId);
            return { conversationLogId, feedbackOptions, userInput };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Unknown error");
        }
    }
);

// ============================================================
// Slice
// ============================================================

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        setActiveChatHistory: (state, action: PayloadAction<string | null>) => {
            state.activeChatHistoryId = action.payload;
        },

        addOptimisticMessage: (state, action: PayloadAction<Message>) => {
            const message = action.payload;
            state.messages[message.id] = message;

            if (!state.messagesByChatHistory[message.conversationId]) {
                state.messagesByChatHistory[message.conversationId] = [];
            }
            state.messagesByChatHistory[message.conversationId].push(message.id);
        },

        updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: Message["status"] }>) => {
            const { messageId, status } = action.payload;
            // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
            if (state.messages[messageId]) {
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.messages[messageId].status = status;
                if (status === "sent") {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.messages[messageId].completedAt = new Date().toISOString();
                }
            }
        },

        appendToMessage: (state, action: PayloadAction<{ messageId: string; content: string }>) => {
            const { messageId, content } = action.payload;
            // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
            if (state.messages[messageId]) {
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.messages[messageId].content += content;
            }
        },

        setStreaming: (state, action: PayloadAction<boolean>) => {
            state.isStreaming = action.payload;
        },

        updateChatHistoryTitle: (state, action: PayloadAction<{ conversationId: string; title: string }>) => {
            const { conversationId, title } = action.payload;
            // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
            if (state.chatHistories[conversationId]) {
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.chatHistories[conversationId].title = title;
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.chatHistories[conversationId].updatedAt = new Date().toISOString();
            }
        },

        clearError: state => {
            state.error = null;
        },

        setAnswers: (state, action: PayloadAction<ChatAnswerTuple[]>) => {
            state.answers = action.payload;
        },

        addAnswer: (state, action: PayloadAction<ChatAnswerTuple>) => {
            state.answers.push(action.payload);
        },

        setStreamedAnswers: (state, action: PayloadAction<ChatAnswerTuple[]>) => {
            state.streamedAnswers = action.payload;
        },

        setLastQuestion: (state, action: PayloadAction<string>) => {
            state.lastQuestion = action.payload;
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        setConversationLogId: (state, action: PayloadAction<string>) => {
            state.conversationLogId = action.payload;
        },

        clearChatState: state => {
            state.lastQuestion = "";
            state.error = null;
            state.answers = [];
            state.streamedAnswers = [];
            state.conversationLogId = "";
        }
    },

    extraReducers: builder => {
        builder
            .addCase(logout, () => initialState)

            .addCase(fetchChatHistories.pending, state => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchChatHistories.fulfilled, (state, action) => {
                state.isLoading = false;
                state.chatHistories = action.payload.reduce(
                    (acc, entry) => {
                        acc[entry.id] = entry;
                        return acc;
                    },
                    {} as Record<string, ChatHistoryEntry>
                );
            })
            .addCase(fetchChatHistories.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchMessages.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const { conversationId, messages } = action.payload;

                messages.forEach(msg => {
                    state.messages[msg.id] = msg;
                });

                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.messagesByChatHistory[conversationId] = messages.map(m => m.id);
            })
            .addCase(fetchMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            .addCase(createChatHistory.fulfilled, (state, action) => {
                const chatHistoryEntry = action.payload;
                state.chatHistories[chatHistoryEntry.id] = chatHistoryEntry;
                state.messagesByChatHistory[chatHistoryEntry.id] = [];
                state.activeChatHistoryId = chatHistoryEntry.id;
            })

            .addCase(sendMessage.pending, state => {
                state.isStreaming = true;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.isStreaming = false;
                const { conversationId, assistantMessage } = action.payload;

                state.messages[assistantMessage.id] = assistantMessage;
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.messagesByChatHistory[conversationId].push(assistantMessage.id);

                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                if (state.chatHistories[conversationId]) {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.chatHistories[conversationId].updatedAt = new Date().toISOString();
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.chatHistories[conversationId].messageCount = state.messagesByChatHistory[conversationId].length;
                }
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.isStreaming = false;
                state.error = action.payload as string;
            })

            .addCase(submitFeedback.fulfilled, (state, action) => {
                const { messageId, feedback } = action.payload;
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                if (state.messages[messageId]) {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.messages[messageId].feedback = feedback;
                }
            })

            .addCase(clearChatHistory.fulfilled, (state, action) => {
                const conversationId = action.payload;

                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                const messageIds = state.messagesByChatHistory[conversationId] || [];
                messageIds.forEach(id => {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    delete state.messages[id];
                });
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                state.messagesByChatHistory[conversationId] = [];

                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                if (state.chatHistories[conversationId]) {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.chatHistories[conversationId].messageCount = 0;
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    state.chatHistories[conversationId].updatedAt = new Date().toISOString();
                }
            })

            .addCase(deleteChatHistory.fulfilled, (state, action) => {
                const conversationId = action.payload;

                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                const messageIds = state.messagesByChatHistory[conversationId] || [];
                messageIds.forEach(id => {
                    // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                    delete state.messages[id];
                });
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                delete state.messagesByChatHistory[conversationId];
                // eslint-disable-next-line security/detect-object-injection -- Redux state lookup with typed action payload
                delete state.chatHistories[conversationId];

                if (state.activeChatHistoryId === conversationId) {
                    state.activeChatHistoryId = null;
                }
            })

            .addCase(sendChatMessage.pending, state => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(sendChatMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isStreaming = false;
                const { question, response } = action.payload;
                state.answers.push([question, response]);
            })
            .addCase(sendChatMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.isStreaming = false;
                state.error = action.payload as string;
            });
    }
});

export const {
    setActiveChatHistory,
    addOptimisticMessage,
    updateMessageStatus,
    appendToMessage,
    setStreaming,
    updateChatHistoryTitle,
    clearError,
    setAnswers,
    addAnswer,
    setStreamedAnswers,
    setLastQuestion,
    setLoading,
    setError,
    setConversationLogId,
    clearChatState
} = chatSlice.actions;

export default chatSlice.reducer;
